import { AsyncSection } from "../components/AsyncSection";
import { Callout } from "../components/Callout";
import { Container } from "../components/Container";
import { DisclosureNote } from "../components/DisclosureNote";
import { LedgerRow } from "../components/LedgerRow";
import { SectionHeading } from "../components/SectionHeading";
import { Skeleton } from "../components/Skeleton";
import { useStaticResource } from "../hooks/useStaticResource";
import { formatClicks, formatMeasuredAt, formatPeriod } from "../lib/format";
import styles from "./ClickRankings.module.css";
import type { RankingDisclosure, RankingResponse } from "../api/types";
import type { ClickSurface } from "../lib/click";
import type { Resource } from "../hooks/useApiResource";

/**
 * Top deals by buyer clicks — the lists money cannot buy.
 *
 * Canon § 11 and § 14 make these the deliberate counterweight to the daily board: founders
 * buy daily exposure, but weekly and monthly position follows qualified buyer clicks. The
 * figure beside every row is therefore a click count, labelled as one, and no bid amount
 * appears anywhere in the section.
 *
 * **One section, two lists, both on the page** (`WEB-033`, canon decision `D-014`). This has
 * now been built three ways. `WEB-012` put the two side by side; `D-012` reversed that and
 * restored the canvas's `Weekly` / `Monthly` pills, which `WEB-027` built; the operator, with
 * both in front of them, has asked for the pair again. `D-014` is that instruction, and it is
 * a decision rather than a note because only a decision can move what `D-012` settled
 * (`AGENTS.md` § 3).
 *
 * This is therefore the one section on the page that departs from the drawing deliberately.
 * What the departure buys is that the contrast canon § 11 draws — what money buys against
 * what buyers decide — is read in one glance rather than one list at a time.
 *
 * **Both periods stay linkable.** `#weekly` and `#monthly` are the fragment ids `D-007`
 * established, and all three decisions since have kept the same condition: neither list may
 * be reachable only by operating something. With both rendered that is true of the lists
 * themselves, and the ids sit on each column's own heading so a reader who followed one lands
 * on the list it names rather than on the section's top.
 *
 * The heading carries an earned mark. It is in the accent, never brass — brass carries bought
 * placement everywhere else on this site, and a list money cannot buy wearing the mark of one
 * that can would say the opposite of what the section means.
 *
 * Ranks here are plain rather than brass, and the ranking's basis is core's own
 * non-sponsorship sentence, verbatim, rather than a paraphrase of it.
 */

type Period = "weekly" | "monthly";

const COPY: Record<
  Period,
  {
    heading: string;
    loadingLabel: string;
    errorTitle: string;
    emptyTitle: string;
    period: string;
    surface: ClickSurface;
  }
> = {
  weekly: {
    heading: "Weekly",
    loadingLabel: "Loading this week’s top deals",
    errorTitle: "This week’s ranking didn’t load",
    emptyTitle: "No qualified clicks yet this week",
    period: "this week",
    surface: "weekly",
  },
  monthly: {
    heading: "Monthly",
    loadingLabel: "Loading this month’s top deals",
    errorTitle: "This month’s ranking didn’t load",
    emptyTitle: "No qualified clicks yet this month",
    period: "this month",
    surface: "monthly",
  },
};

export function ClickRankings({
  weekly: weeklyData,
  monthly: monthlyData,
  builtAt,
}: {
  weekly: RankingResponse;
  monthly: RankingResponse;
  /** When the build read these, in UTC (`WEB-039`). */
  builtAt: string;
}) {
  const weekly = useStaticResource(weeklyData);
  const monthly = useStaticResource(monthlyData);
  const measuredAt = formatMeasuredAt(builtAt);

  /*
   * The basis, stated once where it covers both lists.
   *
   * `contracts/API.md` § Ranking disclosure puts the sentence in each response so that no
   * surface can ship a weaker one, and core sends the same sentence for both periods — it is
   * a statement about how *this kind* of list is ranked. Printing it twice, once per column,
   * would be the same sentence twice on one screen.
   *
   * So it is shared while the two agree, and each column prints its own the moment they do
   * not. That case is not hypothetical housekeeping: the two responses are independent, and a
   * core that revises one period's wording would otherwise have this section showing the old
   * sentence over the new list. The site never composes the text either way.
   */
  const shared =
    weeklyData !== null &&
    monthlyData !== null &&
    weeklyData.disclosure.message === monthlyData.disclosure.message &&
    weeklyData.disclosure.sponsored === monthlyData.disclosure.sponsored
      ? weeklyData.disclosure
      : null;

  return (
    <div className={styles.band}>
      <Container>
        <section className={styles.section} aria-labelledby="by-clicks">
          {/*
            No hard-coded description. The canvas draws one under this heading — "Ranked by
            qualified buyer clicks during this period. Bid amount does not affect this
            ranking." — and that is core's disclosure sentence to the character
            (`contracts/API.md` § Ranking disclosure). Writing it here would print it twice
            once the response landed, and would let the site's copy drift from the contract
            text canon § 12 requires be shown verbatim. So the drawing's description slot is
            filled by `DisclosureNote`, from core.
          */}
          <SectionHeading id="by-clicks" title="Top deals by buyer clicks" aside={<EarnedMark />} />
          {shared === null ? null : <DisclosureNote disclosure={shared} />}

          {/*
            Canon `D-015` § 7. These two lists are the only part of the page whose figures move
            continuously, and the homepage is built rather than read live — so they may be up
            to a build cycle behind. The board and the Founding wall carry no such line: canon
            § 7 makes them day-stable, and a notice on something that cannot be stale would be
            noise. Saying when these were measured costs nothing, and a figure presented as
            current when it is hours old is the kind of small untruth this board exists not to
            tell.
          */}
          {measuredAt === null ? null : (
            <p className={`${styles.measured} figure`}>Clicks counted as at {measuredAt}.</p>
          )}

          {/*
            The pair. Weekly first — the order `D-014` names, and the order they stack in on a
            phone, where side by side would leave two columns too narrow to read a product
            name in.
          */}
          <div className={styles.pair}>
            <RankingList
              period="weekly"
              resource={weekly}
              disclosure={shared === null ? weeklyData.disclosure : undefined}
            />
            <RankingList
              period="monthly"
              resource={monthly}
              disclosure={shared === null ? monthlyData.disclosure : undefined}
            />
          </div>
        </section>
      </Container>
    </div>
  );
}

/**
 * The mark on the click-ranked heading: a laurel, and the words that say what it means.
 *
 * The glyph is `aria-hidden` and the words are not, because the claim is "buyers chose
 * these" and a wreath does not say that on its own. Nothing here implies an auction win or
 * an editorial judgement — canon § 12 — and nothing here is brass.
 */
function EarnedMark() {
  return (
    <span className={styles.earned}>
      <svg className={styles.laurel} viewBox="0 0 20 16" aria-hidden="true" focusable="false">
        <path
          d="M7.4 14.6C4.2 13.4 2.4 10.6 2.6 7 4.9 7.5 6.6 8.7 7.6 10.5M6.2 8.2C4.6 6.6 4.1 4.3 4.9 1.9 6.9 3 8.1 4.6 8.4 6.6M12.6 14.6c3.2-1.2 5-4 4.8-7.6-2.3.5-4 1.7-5 3.5M13.8 8.2c1.6-1.6 2.1-3.9 1.3-6.3-2 1.1-3.2 2.7-3.5 4.7"
          fill="none"
          stroke="currentColor"
          strokeWidth="1.4"
          strokeLinecap="round"
          strokeLinejoin="round"
        />
      </svg>
      Buyer-earned
    </span>
  );
}

/**
 * One period's column: its heading, its bounds, and the rows.
 *
 * The heading carries the fragment id, so `#weekly` and `#monthly` land a reader on the list
 * each names rather than on the section's top. It is an `h3` under the section's `h2` and the
 * column is a plain block rather than a labelled region: the section around it is already the
 * page's landmark for both lists, and two more regions inside it would only make the landmark
 * list longer without telling anyone anything new.
 *
 * `disclosure` is passed only when the two periods' sentences disagree — see `ClickRankings`.
 * Undefined means the section already printed one that covers this list.
 */
function RankingList({
  period,
  resource,
  disclosure,
}: {
  period: Period;
  resource: Resource<RankingResponse>;
  disclosure?: RankingDisclosure | undefined;
}) {
  const copy = COPY[period];
  const data = resource.state.status === "ready" ? resource.state.data : null;

  return (
    <div className={styles.column}>
      <h3 id={period} className={styles.columnHeading}>
        {copy.heading}
      </h3>

      {disclosure === undefined ? null : <DisclosureNote disclosure={disclosure} />}

      {/* Core returns the period bounds so the site never recomputes a week or month
          boundary and labels the ranking with a range it did not actually cover. */}
      {data === null ? null : (
        <p className={styles.bounds}>{formatPeriod(data.period.start, data.period.end)}</p>
      )}

      <AsyncSection
        state={resource.state}
        reload={resource.reload}
        loadingLabel={copy.loadingLabel}
        errorTitle={copy.errorTitle}
        skeleton={
          <div className={styles.skeleton}>
            {[0, 1, 2].map((row) => (
              <Skeleton key={row} height="1.25rem" />
            ))}
          </div>
        }
      >
        {(loaded) => {
          const leaderClicks = loaded.entries[0]?.qualifiedClicks ?? 0;
          return (
            <>
              {/* The disclosure is rendered above the pair, where the canvas draws the
                  section's description — see `ClickRankings` above. */}
              {loaded.entries.length === 0 ? (
                <Callout title={copy.emptyTitle}>
                  Rankings build from buyer clicks as the period runs. Nothing is listed
                  here until there is something real to count.
                </Callout>
              ) : (
                <ol className={styles.entries} role="list">
                  {loaded.entries.map((entry) => (
                    <LedgerRow
                      key={entry.deal.id}
                      rank={entry.rank}
                      deal={entry.deal}
                      figure={formatClicks(entry.qualifiedClicks)}
                      figureLabel={`qualified buyer clicks ${copy.period}`}
                      surface={copy.surface}
                      rankTone="earned"
                      // Against the leader, so the top row is always a full bar. Core
                      // returns the list already ordered by clicks, so that is the first.
                      share={shareOf(entry.qualifiedClicks, leaderClicks)}
                    />
                  ))}
                </ol>
              )}
            </>
          );
        }}
      </AsyncSection>
    </div>
  );
}

/** A row's clicks as a fraction of the leader's, guarding the all-zero list. */
function shareOf(clicks: number, leader: number): number {
  return leader <= 0 ? 0 : clicks / leader;
}
